import createClient, { type Middleware } from "openapi-fetch";

import type { paths } from "./schema";

/** Tokenlar saqlanadigan joy. Web — localStorage, mobil — SecureStore. */
export interface TokenStore {
  getAccess(): string | null;
  getRefresh(): string | null;
  set(access: string, refresh?: string): void;
  clear(): void;
}

export interface ApiOptions {
  baseUrl: string;
  tokens: TokenStore;
  /** Refresh ham o'tmasa (sessiya tugadi) chaqiriladi: login sahifasiga o'tkazish uchun. */
  onAuthLost?: () => void;
  fetch?: (input: Request) => Promise<Response>;
}

const PUBLIC_PATHS = ["/api/auth/login/", "/api/auth/refresh/", "/api/auth/otp/", "/api/setup/", "/api/public-config/", "/api/auth/telegram/", "/api/app/latest/", "/api/app/download/"];
const isPublic = (url: string) => PUBLIC_PATHS.some((p) => new URL(url).pathname.startsWith(p));

export function createApi(opts: ApiOptions) {
  const doFetch = opts.fetch ?? ((r: Request) => fetch(r));
  const client = createClient<paths>({ baseUrl: opts.baseUrl, fetch: doFetch });

  // Bir vaqtda ko'p so'rov 401 olsa, refresh faqat bir marta yuboriladi
  let refreshing: Promise<boolean> | null = null;
  const refreshOnce = () => {
    refreshing ??= (async () => {
      const refresh = opts.tokens.getRefresh();
      if (!refresh) return false;
      try {
        const res = await doFetch(
          new Request(`${opts.baseUrl}/api/auth/refresh/`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh }),
          }),
        );
        if (!res.ok) return false;
        const data = (await res.json()) as { access: string; refresh?: string };
        opts.tokens.set(data.access, data.refresh);
        return true;
      } catch {
        return false;
      } finally {
        setTimeout(() => (refreshing = null), 0);
      }
    })();
    return refreshing;
  };

  // Body bir marta o'qiladi; qayta yuborish uchun nusxasini saqlab qo'yamiz
  const clones = new WeakMap<Request, Request>();

  const middleware: Middleware = {
    onRequest({ request }) {
      if (isPublic(request.url)) return;
      clones.set(request, request.clone());
      const access = opts.tokens.getAccess();
      if (access) request.headers.set("Authorization", `Bearer ${access}`);
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status !== 401 || isPublic(request.url)) return;
      const original = clones.get(request);
      if (!original) return;
      if (!(await refreshOnce())) {
        opts.tokens.clear();
        opts.onAuthLost?.();
        return;
      }
      original.headers.set("Authorization", `Bearer ${opts.tokens.getAccess()}`);
      return doFetch(original);
    },
  };
  client.use(middleware);
  return client;
}

export type ApiClient = ReturnType<typeof createApi>;
